import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core';

// 'card' is the neutral skeleton for when the kind of chart is not known yet (the dashboard's layout is still loading); every chart then keeps its own
// kind's skeleton from the moment it is known until its data arrives.
export type ChartSkeletonVariant = 'card' | 'bars' | 'columns' | 'line' | 'pie' | 'gauge';

// A dashboard card is about this tall once its chart has loaded. The skeleton (and the card around a loading chart) keeps that height so the
// cards below do not jump up while charts load, which would also pull more cards into view and start their queries too early.
export const CHART_CARD_MIN_HEIGHT = 560;

const GREY = '#e3e3e3';
const FILL = `linear-gradient(${GREY}, ${GREY})`;

interface SkeletonLayer {
    image: string;
    position: string;
    size: string;
}

const rect = (position: string, size: string): SkeletonLayer => ({ image: FILL, position, size });
const circle = (position: string, size: string): SkeletonLayer => ({ image: `radial-gradient(circle closest-side, ${GREY} 98%, transparent 100%)`, position, size });

// The shapes are drawn as background layers of ONE element, not as dozens of components: a dashboard panel can hold over two hundred charts, and each of
// them has a skeleton on screen (or waiting to scroll into view) at once.
const headerLayers = (withNumber: boolean): SkeletonLayer[] => [
    rect('18px 16px', '36px 36px'),
    rect('64px 18px', '45% 20px'),
    rect('64px 44px', '24% 14px'),
    ...(withNumber ? [rect('18px 84px', '38% 52px'), rect('18px 148px', '62% 16px')] : []),
];

const footerLayers = (): SkeletonLayer[] => [
    rect('left calc(50% - 98px) bottom 90px', '90px 16px'),
    rect('left calc(50% + 8px) bottom 90px', '90px 16px'),
    rect('left 18px bottom 16px', 'calc(100% - 36px) 40px'),
];

const BAR_FRACTIONS = [0.88, 0.72, 0.61, 0.5, 0.38, 0.27, 0.16];
const COLUMN_HEIGHTS = [170, 92, 126, 72, 150, 106, 82, 140, 64, 116, 88, 132];

const getAreaLayers = (variant: ChartSkeletonVariant): SkeletonLayer[] => {
    switch (variant) {
        case 'bars':
            return BAR_FRACTIONS.reduce<SkeletonLayer[]>(
                (layers, fraction, index) => [
                    ...layers,
                    rect(`18px ${190 + index * 40}px`, '90px 16px'),
                    rect(`118px ${189 + index * 40}px`, `calc(${fraction} * (100% - 136px)) 14px`),
                ],
                [],
            );
        case 'columns':
            return COLUMN_HEIGHTS.map((height, index) =>
                rect(`left calc(18px + ((100% - 36px) / 12) * ${index}) bottom 130px`, `calc(((100% - 36px) / 12) - 8px) ${height}px`),
            );
        case 'pie':
            return [circle('center 190px', '220px 220px')];
        case 'gauge':
            return [
                rect('18px 84px', 'calc(50% - 24px) 96px'),
                rect('right 18px top 84px', 'calc(50% - 24px) 96px'),
                { image: `radial-gradient(circle 120px at 50% 100%, ${GREY} 98%, transparent 100%)`, position: 'center 230px', size: '240px 120px' },
            ];
        default:
            return [rect('18px 190px', 'calc(100% - 36px) calc(100% - 330px)')];
    }
};

const buildStyle = (variant: ChartSkeletonVariant): React.CSSProperties => {
    const layers = [...headerLayers(variant !== 'gauge'), ...getAreaLayers(variant), ...footerLayers()];
    return {
        backgroundImage: layers.map((layer) => layer.image).join(', '),
        backgroundPosition: layers.map((layer) => layer.position).join(', '),
        backgroundSize: layers.map((layer) => layer.size).join(', '),
        backgroundRepeat: 'no-repeat',
    };
};

// Built once per kind of chart.
const STYLE_BY_VARIANT: { [variant: string]: React.CSSProperties } = {};
const getStyle = (variant: ChartSkeletonVariant): React.CSSProperties => {
    if (!STYLE_BY_VARIANT[variant]) {
        STYLE_BY_VARIANT[variant] = buildStyle(variant);
    }
    return STYLE_BY_VARIANT[variant];
};

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: CHART_CARD_MIN_HEIGHT,
            boxSizing: 'border-box',
        },
        layer: {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        },
        pulse: {
            animation: '$rvChartSkeletonPulse 1.5s ease-in-out 0.5s infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes rvChartSkeletonPulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.4 },
            '100%': { opacity: 1 },
        },
    }),
);

interface ChartSkeletonProps {
    variant?: ChartSkeletonVariant;
    // Cards still waiting to scroll into view are not animated (there can be hundreds of them); a loading chart's skeleton pulses.
    animated?: boolean;
}

// The loading state of a dashboard chart card: the same header, headline number, chart area, legend and period switcher a loaded chart has, drawn in
// grey. The middle follows the kind of chart (horizontal bars, columns, line, pie, gauge), so each chart type has one shared skeleton.
export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ variant = 'bars', animated = true }) => {
    const classes = useStyles();

    return (
        <div className={classes.root} aria-busy={true}>
            <div className={`${classes.layer}${animated ? ` ${classes.pulse}` : ''}`} style={getStyle(variant)} />
        </div>
    );
};
